import React from "react";
import PropTypes from "prop-types";
import { Row, Col, Card, CardBody } from "reactstrap";
import { usePageTitle } from "../ui/PageHeader";
import { isReadOnlyMode } from "../../../helpers/readOnlyMode";

/**
 * 唯讀詳情頁的共用版面（ticket 02）。
 *
 * 各模組的詳情頁長得一樣:標題與返回鍵在 topbar + 右側動作 → 數個「標題 + 欄位格」卡片。
 * 先前 審核詳情 / 個人資料 / 工作項目 各自複製了一份 label/value 小元件,
 * 這裡收成一組,新模組直接沿用。
 *
 *   <DetailView title="機櫃詳情" onBack={...} actions={<Button/>}>
 *     <DetailSection title="機櫃資訊">
 *       <DetailFields>
 *         <DetailField label="機櫃編號">R-A01</DetailField>
 *       </DetailFields>
 *     </DetailSection>
 *   </DetailView>
 */
export const DetailView = ({ title, onBack, actions, children }) => {
  // 標題與返回鍵都發佈到 topbar —— Figma v2(2128:23479)把返回箭頭放在標題左邊
  // 同一個群組,所以頁內既不重複標題、也不再自己畫一列返回鍵,只留右側動作區。
  usePageTitle(typeof title === "string" ? title : undefined, onBack);
  // UAT 唯讀模式(helpers/readOnlyMode):詳情頁右上那排全是寫入
  // (編輯／送出審核／結案／撤回／回報上架),整排不渲染。
  const showActions = actions && !isReadOnlyMode();
  return (
    <>
      {showActions ? (
        <div className="d-flex align-items-center justify-content-end flex-wrap gap-2 mb-3">
          {actions}
        </div>
      ) : null}
      {children}
    </>
  );
};
DetailView.propTypes = {
  title: PropTypes.node,
  onBack: PropTypes.func,
  // 返回鍵改到 topbar 之後只剩箭頭沒有文字;保留 prop 相容既有呼叫端,不渲染
  backLabel: PropTypes.string,
  actions: PropTypes.node,
  children: PropTypes.node,
};

/** 詳情頁的一張卡片（標題可省略） */
export const DetailSection = ({ title, actions, children, className = "mb-3" }) => (
  <Card className={className}>
    <CardBody>
      {title || actions ? (
        <div className="d-flex align-items-center justify-content-between mb-2">
          {title ? <div className="text-muted small">{title}</div> : <span />}
          {actions}
        </div>
      ) : null}
      {children}
    </CardBody>
  </Card>
);
DetailSection.propTypes = {
  title: PropTypes.node,
  actions: PropTypes.node,
  children: PropTypes.node,
  className: PropTypes.string,
};

/** 欄位格容器 */
export const DetailFields = ({ children }) => <Row>{children}</Row>;
DetailFields.propTypes = { children: PropTypes.node };

/**
 * 單一唯讀欄位:上標題下內容;空值一律顯示破折號,不留空白讓人以為漏了。
 * 字級跟表單欄位同一組(DCK-BUG-017):檢視與編輯切換時字不能忽大忽小。
 */
export const DetailField = ({ label, span = 4, children }) => {
  const empty = children === null || children === undefined || children === "";
  return (
    <Col md={span} className="mb-3 dck-detail-field">
      <div className="dck-detail-field__label">{label}</div>
      <div className="dck-detail-field__value">{empty ? "—" : children}</div>
    </Col>
  );
};
DetailField.propTypes = {
  label: PropTypes.node,
  span: PropTypes.number,
  children: PropTypes.node,
};

export default DetailView;
